import React, { useState, useEffect } from 'react';
import { Shield, Lock, Activity, FileText, AlertCircle, Zap, Eye, Clock, Server, Globe } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api';
const getToken = () => localStorage.getItem('accessToken') || localStorage.getItem('token');

interface AuditLog {
  id: string;
  _id?: string;
  action: string;
  user: string;
  ipAddress: string;
  userAgent?: string;
  details?: string;
  createdAt: string;
}

export default function SecurityTab() {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'overview' | 'logs'>('overview');

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/enterprise/sso/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.success) {
        setAuditLogs(data.logs || data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const securityFeatures = [
    {
      icon: Shield,
      title: 'Helmet.js Protection',
      desc: 'CSP, HSTS, XSS Protection, NoSniff',
      status: 'active' as const,
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#86efac',
      text: '#166534'
    },
    {
      icon: Lock,
      title: 'CORS Security',
      desc: 'Whitelist-only origin validation',
      status: 'active' as const,
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#86efac',
      text: '#166534'
    },
    {
      icon: Activity,
      title: 'Rate Limiting',
      desc: '100 req/15min general, 10 req/hr payments',
      status: 'active' as const,
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#86efac',
      text: '#166534'
    },
    {
      icon: FileText,
      title: 'CSRF Protection',
      desc: 'Token-based mutation security',
      status: 'active' as const,
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#86efac',
      text: '#166534'
    },
    {
      icon: AlertCircle,
      title: 'SQL Injection Prevention',
      desc: 'Input sanitization & parameterized queries',
      status: 'active' as const,
      color: '#d97706',
      bg: '#fef3c7',
      border: '#fcd34d',
      text: '#92400e'
    },
    {
      icon: Zap,
      title: 'Payload Limits',
      desc: '10MB request body limit enforced',
      status: 'active' as const,
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#86efac',
      text: '#166534'
    },
    {
      icon: Globe,
      title: 'HTTPS Enforcement',
      desc: 'TLS 1.2+ with HSTS preload',
      status: 'active' as const,
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#86efac',
      text: '#166534'
    },
    {
      icon: Server,
      title: 'Session Security',
      desc: 'HttpOnly, Secure cookies, 24h expiry',
      status: 'active' as const,
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#86efac',
      text: '#166534'
    },
  ];

  return (
    <div>
      {/* Header with Toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', margin: 0 }}>
          <Shield size={20} />
          Security Monitor
        </h3>
        <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', borderRadius: '8px', padding: '3px' }}>
          <button
            onClick={() => setActiveView('overview')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeView === 'overview' ? '600' : '400',
              background: activeView === 'overview' ? 'white' : 'transparent',
              color: activeView === 'overview' ? '#111827' : '#6b7280',
              boxShadow: activeView === 'overview' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <Shield size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
            Overview
          </button>
          <button
            onClick={() => setActiveView('logs')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeView === 'logs' ? '600' : '400',
              background: activeView === 'logs' ? 'white' : 'transparent',
              color: activeView === 'logs' ? '#111827' : '#6b7280',
              boxShadow: activeView === 'logs' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <Clock size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
            Audit Logs
          </button>
        </div>
      </div>

      {/* Overview View */}
      {activeView === 'overview' && (
        <>
          {/* Security Features Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '14px',
            marginBottom: '24px'
          }}>
            {securityFeatures.map((feature, i) => (
              <div
                key={i}
                style={{
                  background: feature.bg,
                  padding: '18px',
                  borderRadius: '12px',
                  border: `1px solid ${feature.border}`,
                  transition: 'transform 0.15s, box-shadow 0.15s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <feature.icon size={20} color={feature.color} />
                  <strong style={{ color: feature.text, fontSize: '14px' }}>{feature.title}</strong>
                </div>
                <p style={{ fontSize: '12px', color: feature.text, margin: '0 0 10px 0', opacity: 0.8 }}>
                  {feature.desc}
                </p>
                <span style={{
                  background: feature.color,
                  color: 'white',
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {feature.status}
                </span>
              </div>
            ))}
          </div>

          {/* Security Score */}
          <div style={{
            background: 'linear-gradient(135deg, #10b981, #059669)',
            padding: '28px',
            borderRadius: '14px',
            color: 'white',
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            <Shield size={44} style={{ marginBottom: '12px', opacity: 0.9 }} />
            <div style={{ fontSize: '56px', fontWeight: '800', lineHeight: '1' }}>A+</div>
            <div style={{ fontSize: '16px', marginTop: '4px', opacity: 0.9 }}>Security Score</div>
            <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>
              All security measures active and verified
            </div>
          </div>

          {/* Quick Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
            <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>8</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Active Protections</div>
            </div>
            <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#2563eb' }}>24/7</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Monitoring</div>
            </div>
            <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#7c3aed' }}>0</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Breaches Detected</div>
            </div>
            <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#d97706' }}>100%</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Uptime</div>
            </div>
          </div>
        </>
      )}

      {/* Audit Logs View */}
      {activeView === 'logs' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '600', margin: 0 }}>
              <Clock size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Real-time Request Monitor
            </h4>
            <button
              onClick={fetchAuditLogs}
              disabled={loading}
              style={{
                padding: '6px 12px',
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#6b7280'
              }}
            >
              {loading ? 'Refreshing...' : '🔄 Refresh'}
            </button>
          </div>

          <div style={{
            background: '#1e1e2e',
            padding: '20px',
            borderRadius: '12px',
            color: '#cdd6f4',
            fontFamily: '"Fira Code", "Cascadia Code", monospace',
            fontSize: '12px',
            maxHeight: '400px',
            overflowY: 'auto',
            lineHeight: '1.8'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c7086' }}>
                <Activity size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p>Loading audit logs...</p>
              </div>
            ) : auditLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c7086' }}>
                <Eye size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p>No requests logged yet</p>
                <p style={{ fontSize: '11px', opacity: 0.6 }}>Activity will appear here in real-time</p>
              </div>
            ) : (
              auditLogs.map((log, i) => (
                <div
                  key={log.id || log._id || i}
                  style={{
                    padding: '5px 0',
                    borderBottom: '1px solid #313244',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}
                >
                  <span>
                    <span style={{ color: '#89b4fa', fontWeight: '600' }}>
                      [{log.action?.toUpperCase() || 'REQUEST'}]
                    </span>{' '}
                    <span style={{ color: '#a6e3a1' }}>{log.user || 'anonymous'}</span>
                    {' → '}
                    <span style={{ color: '#f9e2af' }}>{log.ipAddress || '127.0.0.1'}</span>
                    {log.details && (
                      <span style={{ color: '#6c7086', marginLeft: '8px' }}>
                        ({log.details})
                      </span>
                    )}
                  </span>
                  <span style={{ color: '#6c7086', fontSize: '11px' }}>
                    {new Date(log.createdAt).toLocaleTimeString()}
                  </span>
                </div>
              ))
            )}
          </div>

          <div style={{
            marginTop: '12px',
            padding: '12px 16px',
            background: '#f9fafb',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <AlertCircle size={14} />
            Logs are retained for 30 days. Export coming soon.
          </div>
        </div>
      )}
    </div>
  );
}